import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "icon",
    "progressBar",
    "progressContainer",
    "progressText",
    "statusBadge",
    "errorPanel",
    "errorMessage",
  ]

  static values = {
    statementId: String,
    clientId: String,
    terminal: Boolean,
    refreshInterval: { type: Number, default: 2000 },
    progressRowsTemplate: { type: String, default: "{processed} / {total} rows" },
    rowsExtractedTemplate: { type: String, default: "{count} rows extracted" },
    redirectingText: { type: String, default: "Redirecting..." },
    statusColors: Object,
    statusLabels: Object,
    analyzingSteps: Array,
    redirectUrl: String,
    previewUrl: String,
  }

  connect() {
    if (this.terminalValue) return
    this.#maybeStartMessageCycle()
    const jitter = Math.floor(Math.random() * 500)
    this.timeoutId = setTimeout(() => this.poll(), jitter)
  }

  disconnect() {
    if (this.timeoutId) clearTimeout(this.timeoutId)
    this.#stopMessageCycle()
  }

  async poll() {
    try {
      const response = await fetch(
        `/clients/${this.clientIdValue}/statements/${this.statementIdValue}/processing_status`,
        {
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        this.#updateUI(data)

        if (data.status === "pending" || data.status === "processing") {
          this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
        } else {
          this.#stopMessageCycle()
          this.#handleTerminal(data)
        }
      } else {
        this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
      }
    } catch (error) {
      console.error("Error polling processing status:", error)
      this.timeoutId = setTimeout(() => this.poll(), this.refreshIntervalValue)
    }
  }

  #updateUI(data) {
    if (this.hasIconTarget && data.thumb_url) {
      this.#showThumb(data.thumb_url)
    }

    if (data.row_count) {
      this.#switchToDeterminate()
      if (this.hasProgressBarTarget && data.progress !== undefined) {
        this.progressBarTarget.style.width = `${Math.max(data.progress, 5)}%`
      }
    }

    if (this.hasProgressTextTarget) {
      if (data.status === "completed") {
        this.progressTextTarget.textContent = this.rowsExtractedTemplateValue
          .replace("{count}", data.processed_count || data.row_count || 0)
      } else if (data.row_count) {
        this.progressTextTarget.textContent = this.progressRowsTemplateValue
          .replace("{processed}", data.processed_count || 0)
          .replace("{total}", data.row_count)
      }
    }

    if (this.hasStatusBadgeTarget && data.status) {
      this.#updateStatusBadge(data.status)
    }

    if (this.hasErrorPanelTarget && this.hasErrorMessageTarget) {
      if (data.status === "failed" && data.error_message) {
        this.errorMessageTarget.textContent = data.error_message
        this.errorPanelTarget.classList.remove("hidden")
      }
    }
  }

  #showThumb(url) {
    if (this.iconTarget.querySelector("img")) return
    const img = document.createElement("img")
    img.src = url
    img.alt = ""
    img.className = "w-full h-full object-cover"
    this.iconTarget.replaceChildren(img)
  }

  #switchToDeterminate() {
    if (!this.hasProgressContainerTarget) return
    if (this.progressContainerTarget.classList.contains("statement-progress-indeterminate")) {
      this.progressContainerTarget.classList.remove("statement-progress-indeterminate")
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.classList.remove("hidden")
      }
      this.#stopMessageCycle()
    }
  }

  #updateStatusBadge(status) {
    const badge = this.statusBadgeTarget
    const colors = this.hasStatusColorsValue ? this.statusColorsValue : {}
    const labels = this.hasStatusLabelsValue ? this.statusLabelsValue : {}

    const colorClass = colors[status]
    if (colorClass) {
      Object.values(colors).forEach((cls) => {
        cls.split(" ").forEach((c) => badge.classList.remove(c))
      })
      colorClass.split(" ").forEach((c) => badge.classList.add(c))
    }

    if (labels[status]) badge.textContent = labels[status]
  }

  #handleTerminal(data) {
    if (data.status === "completed" && this.redirectUrlValue) {
      this.#showRedirectingMessage()
      setTimeout(() => {
        window.location.href = this.redirectUrlValue
      }, 1200)
    } else if (data.status === "preview" && this.previewUrlValue) {
      this.#showRedirectingMessage()
      setTimeout(() => {
        window.location.href = this.previewUrlValue
      }, 1200)
    }
  }

  #showRedirectingMessage() {
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = this.redirectingTextValue
    }
  }

  #maybeStartMessageCycle() {
    if (!this.hasAnalyzingStepsValue) return
    const steps = this.analyzingStepsValue
    if (!Array.isArray(steps) || steps.length < 2) return
    if (!this.hasProgressContainerTarget) return
    if (!this.progressContainerTarget.classList.contains("statement-progress-indeterminate")) return

    let idx = 0
    this.messageCycleId = setInterval(() => {
      idx = (idx + 1) % steps.length
      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = steps[idx]
      }
    }, 1800)
  }

  #stopMessageCycle() {
    if (this.messageCycleId) {
      clearInterval(this.messageCycleId)
      this.messageCycleId = null
    }
  }
}
